import { useState } from 'react'
import { fmtBytes, fmtTime } from '../api'
import { useT } from '../lang'
import { useApi } from '../useApi'
import { Link } from '../router'
import { PageHead } from '../components/Shell'
import { Empty, ErrorBox, Rows } from '../components/bits'

interface AuditRow {
  id: number
  actor: string | null
  entity: string
  entity_id: string | null
  action: string
  before: unknown
  after: unknown
  at: string
}

interface Conflict {
  key: string
  kind: string
  mission_id: string
  start_utc: string | null
  size: number | null
  state: string
  presigned_at: string
  mission_state: string | null
  mission_started_at: string | null
  mission_ended_at: string | null
}

/** Two things that both mean "a human should look at this": the record of who
 *  changed what (R3.9), and data that arrived claiming a mission which was not
 *  active when it was recorded (R1.8). */
export function Activity() {
  const t = useT()
  const [tab, setTab] = useState<'audit' | 'conflicts'>('audit')
  const audit = useApi<{ items: AuditRow[] }>(tab === 'audit' ? '/v1/audit?limit=150' : null)
  const conflicts = useApi<{ items: Conflict[]; total: number }>(
    tab === 'conflicts' ? '/v1/attribution-conflicts?limit=100' : null,
  )

  return (
    <>
      <PageHead
        title={t('Delivery')}
      />

      <div className="seg" style={{ marginBottom: 24 }} role="tablist">
        <button role="tab" aria-selected={tab === 'audit'} onClick={() => setTab('audit')}>
          Changes
        </button>
        <button role="tab" aria-selected={tab === 'conflicts'} onClick={() => setTab('conflicts')}>
          Attribution
          {conflicts.data && conflicts.data.total > 0 ? ` (${conflicts.data.total})` : ''}
        </button>
      </div>

      {tab === 'audit' && (
        <div className="card flush">
          <ErrorBox error={audit.error} />
          {audit.cold ? (
            <Rows />
          ) : (audit.data?.items.length ?? 0) === 0 ? (
            <Empty title="Nothing recorded yet" />
          ) : (
            <div className="scroll-x">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>When</th><th>Who</th><th>Action</th><th>Entity</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.data!.items.map((r) => (
                    <tr key={r.id}>
                      <td data-l="When" className="num faint" style={{ whiteSpace: 'nowrap' }}>{fmtTime(r.at)}</td>
                      <td data-l="Who">{r.actor ?? <span className="faint">—</span>}</td>
                      <td data-l="Action"><span className="badge">{r.action}</span></td>
                      <td data-l="Entity">
                        <span className="faint">{r.entity}</span>{' '}
                        {r.entity_id && r.entity === 'mission' ? (
                          <Link className="mono" to={`/missions/${encodeURIComponent(r.entity_id)}`}>
                            {r.entity_id}
                          </Link>
                        ) : (
                          <span className="mono faint">{r.entity_id}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'conflicts' && (
        <div className="stack">
          <div className="notice">
            Data whose declared mission was not active at its recorded time is
            stored and flagged, never discarded — it may be the only copy.
            Resolving one means correcting the mission window, not deleting the
            object.
          </div>
          <div className="card flush">
            <ErrorBox error={conflicts.error} />
            {conflicts.cold ? (
              <Rows />
            ) : (conflicts.data?.items.length ?? 0) === 0 ? (
              <Empty title="Nothing flagged">
                Everything that has arrived was recorded inside the window of the
                mission it declared.
              </Empty>
            ) : (
              <div className="scroll-x">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Object</th><th>Recorded</th><th>Declared mission</th>
                      <th>Mission window</th><th className="r">Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conflicts.data!.items.map((c) => (
                      <tr key={c.key}>
                        <td data-l="Object">
                          <div>{c.key.split('/').pop()}</div>
                          <div className="faint" style={{ fontSize: 12 }}>{c.kind}</div>
                        </td>
                        <td data-l="Recorded" className="num faint">{fmtTime(c.start_utc)}</td>
                        <td data-l="Declared mission">
                          <Link className="mono" to={`/missions/${encodeURIComponent(c.mission_id)}`}>
                            {c.mission_id}
                          </Link>
                        </td>
                        <td data-l="Mission window" className="num faint">
                          {fmtTime(c.mission_started_at)} →{' '}
                          {c.mission_ended_at ? fmtTime(c.mission_ended_at) : 'open'}
                        </td>
                        <td data-l="Size" className="r num">{fmtBytes(c.size)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
